import { useMemo, useState } from "react";
import { getCarrierStepPreview } from "@aam/shared";
import { cn } from "../lib/cn.js";

interface CarrierStepPreviewProps {
  carriers: string[];
  className?: string;
}

export function CarrierStepPreview({ carriers, className }: CarrierStepPreviewProps) {
  const [open, setOpen] = useState(false);
  const preview = useMemo(() => getCarrierStepPreview(carriers), [carriers.join("|")]);

  const content = (
    <div className="rounded-xl border border-border-default bg-surface-overlay p-4 space-y-4">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-content-muted mb-1">What comes next</p>
        <p className="text-sm text-content-secondary">
          {preview.carrierCount > 0
            ? `${preview.carrierCount} carrier${preview.carrierCount === 1 ? "" : "s"} selected`
            : "Select carriers to preview upcoming steps"}
        </p>
        {preview.estimatedMinutes > 0 && (
          <p className="text-xs text-content-muted mt-1">~{preview.estimatedMinutes} min to complete</p>
        )}
      </div>

      {preview.sections.length > 0 && (
        <div>
          <p className="text-xs font-medium text-content-muted mb-2">Upcoming sections</p>
          <ol className="space-y-1.5">
            {preview.sections.map((title) => (
              <li key={title} className="text-sm text-content-primary flex items-start gap-2">
                <span className="text-accent shrink-0 mt-0.5">•</span>
                <span>{title}</span>
              </li>
            ))}
          </ol>
        </div>
      )}

      {preview.checklist.length > 0 && (
        <div>
          <p className="text-xs font-medium text-content-muted mb-2">Have ready</p>
          <ul className="space-y-1">
            {preview.checklist.map((item) => (
              <li key={item} className="text-xs text-content-secondary flex items-start gap-2">
                <span className="text-status-active-text shrink-0">✓</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );

  return (
    <>
      <div className={cn("hidden lg:block", className)}>{content}</div>

      <div className={cn("lg:hidden", className)}>
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          className="w-full flex items-center justify-between rounded-xl border border-border-default bg-surface-overlay px-4 py-3 text-sm text-content-primary"
        >
          <span>Preview upcoming steps</span>
          <svg
            className={cn("w-4 h-4 text-content-muted transition-transform", open && "rotate-180")}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {open && <div className="mt-3">{content}</div>}
      </div>
    </>
  );
}
